import { v4 as uuidv4 } from 'uuid';

import { Child } from '../interface/Child';

/**
 * Normalise a dateOfBirth value (ISO string or Firestore Timestamp-like object)
 * to a JS Date.
 */
export function dobToDate(dob: Child['dateOfBirth'] | undefined | null): Date | null {
    if (!dob) return null;
    if (typeof dob === 'string') {
        const d = new Date(dob);
        return isNaN(d.getTime()) ? null : d;
    }
    // Firestore Timestamp-like: { seconds, nanoseconds }
    if (typeof dob === 'object' && 'seconds' in dob) {
        return new Date((dob as { seconds: number }).seconds * 1000);
    }
    return null;
}

/**
 * Convert a dateOfBirth value to a YYYY-MM-DD display string.
 */
export function dobToDisplayString(dob: Child['dateOfBirth'] | undefined | null): string {
    const d = dobToDate(dob);
    if (!d) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * Calculate age from a dateOfBirth value.
 */
export function getAgeFromDOB(dob: Child['dateOfBirth'] | undefined | null): number | string {
    const d = dobToDate(dob);
    if (!d) return '—';
    const today = new Date();
    let age = today.getFullYear() - d.getFullYear();
    const m = today.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
    return age;
}

/**
 * Create a blank child for a given parent, with UUID and all required fields.
 */
export function createDefaultChild(parentId: string, parentFirstName = '', parentLastName = '', parentPhone = ''): Child {
    return {
        accountType: 'child',
        userId: uuidv4(),
        parentId,
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        profileImage: '',
        interests: [],
        allergies: [],
        medicalNotes: '',
        emergencyContact: {
            firstName: parentFirstName,
            lastName: parentLastName,
            phone: parentPhone,
            relationship: 'Parent',
        },
        deleted: false,
    };
}
